import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PersonalInsightsComponent } from '@app/insights/personal-insights/personal-insights.component';
import { TabNavigationService } from '@app/navigation/components/tab-navigation/tab-navigation.service';
import { TabNavigationItem } from '@app/navigation/navigation.model';
import { ProfileBaseGuard } from '@app/profile/guards/profile-base.guard';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { DocumentTitleService } from '@app/shared/services/document-title/document-title.service';
import { WindowToken } from '@app/shared/window.token';
import { TagsService } from '@app/tags/services/tags.service';
import { TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs/operators';
import { ProfileOverviewGuard } from '../../guards/profile-overview.guard';
import { ProfileOverviewGuardV2 } from '../../guards/profile-overview-v2.guard';
import { Options, Profile } from '../../profile.model';
import { ProfileCollectionComponent } from '../profile-collection/profile-collection.component';
import { ProfileCollectionComponentV1 } from '../profile-collection-v1/profile-collection.component';
import { ProfileSkillSignalPage } from '../profile-skill-signal-page/profile-skill-signal-page.component';
import { ProfileSkillsComponent } from '../profile-skills/profile-skills.component';
import { ProfileFlexEdGuard } from './../../guards/profile-flex-ed-guard';
import { ProfileOverviewComponent } from './../profile-overview/profile-overview.component';
import { ProfileOverviewComponent as ProfileOverviewComponentV2 } from './../profile-overview-v2/profile-overview.component';
import { ProfileSettings } from '@app/profile-settings/components/profile-settings.component';
import { ProfileSettingsLogin } from '@app/profile-settings/components/profile-settings-login/profile-settings-login.component';
import { ManagerOfManagerService } from '@app/team/services/manager-of-manager.service';
import { AuthService } from '@app/shared/services/auth.service';
import { ProfileService } from '@app/profile/services/profile.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { WindowLayoutService } from '@app/shared/services/window-layout/window-layout.service';
import { GlobalNavigationService } from '@app/shared/services/global-navigation.service';
import { LDFlagsService } from '@app/shared/services/ld-flags.service';
import { ProfileSkillSignalPageV2 } from '@app/profile/components/profile-skill-signals-page-v2/profile-skill-signal-page.component';
import { ProfileCollectionV1Guard } from '@app/profile/guards/profile-collection-v1.guard';
import { ProfileCollectionV2Guard } from '@app/profile/guards/profile-collection-v2.guard';

/**
 * Host component for /profile/:id route. Nested child components get injected in the <router-outlet></router-outlet>
 * at runtime when a user clicks a Tab or activates a child route.
 */
@Component({
  selector: 'dgx-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  public isLoading: boolean;
  public hasProfileHeader: boolean;
  public hasTabNavigation: boolean;
  public options: Options;
  public profile: Readonly<Profile>;
  public tabList: TabNavigationItem[];
  public isSettingsPage: boolean;

  public i18n = this.translateService.instant([
    'Core_Activity',
    'Core_Plans',
    'FlexEdCtrl_FlexEdTitle',
    'PathwayEnrollmentSource_Assignments',
    'UserController_Collection',
    'UserController_GroupsTitle',
    'UserController_Insights',
    'UserController_Opportunities',
    'UserController_Overview',
    'UserController_Pathways',
    'UserController_Progression',
    'UserController_RecommendationsTitle',
    'UserController_Saved',
    'UserController_Skills',
    'dgSkipNav_SkipSubNavigation',
  ]);

  public isMobile: boolean;
  public useUpdatedOverview: boolean;
  // based on the 'disableThirdPartyExperienceManagement' permission, if true add a div with the classname
  // 'reduce-tracking-enabled' so the automated tests can pick this up.
  public usingReducedTracking: boolean;

  public get hideNavigation(): boolean {
    return this.ldFlagsService.lxpLayoutRefresh;
  }

  public get vanityUrl(): string {
    return this.activatedRoute.snapshot.params['id'];
  }

  constructor(
    private documentTitleService: DocumentTitleService,
    private profileBaseGuard: ProfileBaseGuard,
    private profileFlexEdGuard: ProfileFlexEdGuard,
    private profileOverviewGuard: ProfileOverviewGuard,
    private profileOverviewGuardV2: ProfileOverviewGuardV2,
    private profileCollectionGuardV1: ProfileCollectionV1Guard,
    private profileCollectionGuardV2: ProfileCollectionV2Guard,
    private router: Router,
    private translateService: TranslateService,
    private tabNavigationService: TabNavigationService,
    private tagsService: TagsService,
    private trackerService: TrackerService,
    private changeDetectorRef: ChangeDetectorRef,
    private managerOfManagerService: ManagerOfManagerService,
    private auth: AuthService,
    private profileService: ProfileService,
    private windowLayoutService: WindowLayoutService,
    private globalNavigationService: GlobalNavigationService,
    private activatedRoute: ActivatedRoute,
    private ldFlagsService: LDFlagsService,
    @Inject(WindowToken) private windowRef: Window
  ) {
    super();
  }

  public ngOnInit(): void {
    this.initializeHeaderNavigationAndTitle();

    this.isLoading = true;
    this.profileService
      .getProfileDetails(this.vanityUrl)
      .pipe(take(1))
      .subscribe((profileInput) => {
        this.options = profileInput.options;
        this.profile = profileInput.profile;
        this.isLoading = false;
      });

    this.usingReducedTracking =
      this.auth?.authUser?.defaultOrgInfo?.settings?.disableThirdPartyExperienceManagement;
    this.isMobile = this.windowLayoutService.isMobile;
    this.windowLayoutService.windowResize$
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.isMobile = this.windowLayoutService.isMobile;
      });

    this.trackerService.setLocation('Profile');

    this.useUpdatedOverview = this.ldFlagsService.showProfileOverviewV2;

    // check why this is here as it's not doing anything except making redundant API calls?
    const orgId = this.auth.authUser?.defaultOrgInfo?.organizationId;
    if (orgId) {
      this.managerOfManagerService.getHasManager(orgId).subscribe();
    }

    this.isSettingsPage =
      !!this.windowRef.location?.pathname &&
      (this.windowRef.location.pathname.includes('/settings/') ||
        this.windowRef.location.pathname.includes('/settings-login'));
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        // Scroll to the top on route changes
        this.windowRef.scrollTo(0, 0);
      });

    this.hasProfileHeader = true;
    this.hasTabNavigation = true;
    this.tabList = this.getTabs();

    // Set the initial document title
    this.documentTitleService.reload();

    // Hide skills checkup SINA message if user updates ratings
    this.tagsService.userTagsModified
      .pipe(this.takeUntilDestroyed())
      .subscribe((payload) => {
        if (
          this.profileService.ownerIsViewing &&
          payload?.setHasSkillsCheckup
        ) {
          this.options.hasSkillsCheckup = false;
        }
      });
  }

  public ngOnDestroy(): void {
    this.globalNavigationService.reset();
  }

  /**
   * Emits any time a new component is instantiated
   * @param component
   */
  public onActivate(component: any): void {
    this.hasProfileHeader = true;

    this.hasTabNavigation = true;
    // Obtain the class name at runtime and bind the appropriate Inputs
    switch (component.CLASS_NAME) {
      case 'PersonalInsightsComponent':
        component = component as PersonalInsightsComponent;
        component.owner = { ...this.profile };
        break;
      case 'ProfileCollectionComponent':
        component = component as ProfileCollectionComponent;
        component.options = { ...this.options };
        component.owner = { ...this.profile };
        break;
      case 'ProfileCollectionComponentV1':
        component = component as ProfileCollectionComponentV1;
        component.options = { ...this.options };
        component.owner = { ...this.profile };
        break;
      case 'ProfileOverviewComponent':
        component = component as ProfileOverviewComponent;
        component.isEngaged = this.options.isEngaged;
        component.privacyId = this.options.privacyId;
        component.owner = { ...this.profile };
        break;
      case 'ProfileOverviewComponentV2':
        component = component as ProfileOverviewComponentV2;
        component.isEngaged = this.options.isEngaged;
        component.privacyId = this.options.privacyId;
        component.owner = { ...this.profile };
        break;
      case 'ProfileSkillSignalPage':
        component = component as ProfileSkillSignalPage;
        component.owner = { ...this.profile };
        this.hasProfileHeader = false;
        break;
      case 'ProfileSkillSignalPageV2':
        component = component as ProfileSkillSignalPageV2;
        component.owner = { ...this.profile };
        this.hasProfileHeader = false;
        break;
      case 'ProfileSkillsComponent':
        component = component as ProfileSkillsComponent;
        component.owner = { ...this.profile };
        component.hasSkillsCheckup = this.options.hasSkillsCheckup;
        component.isSkillAnalyticsProfile =
          this.options.isSkillAnalyticsProfile;
        component.isSkillAnalyticsProfile =
          this.options.isSkillInventoryProfile;
        component.showSignalsColumn = this.profileOverviewGuard.hasFeature;
        break;
      case 'ProfileSettings':
        component = component as ProfileSettings;
        this.hasProfileHeader = false;
        // without this an ExpressionChangedAfterItHasBeenCheckedError is thrown on the change to hasProfileHeader
        this.changeDetectorRef.detectChanges();
        break;
      case 'ProfileSettingsLogin':
        component = component as ProfileSettingsLogin;
        this.hasProfileHeader = false;
        // without this an ExpressionChangedAfterItHasBeenCheckedError is thrown on the change to hasProfileHeader
        this.changeDetectorRef.detectChanges();
        break;
      default:
        break;
    }
  }

  /**
   * Get the secondary tab navigation
   * @returns
   */
  private getTabs(): TabNavigationItem[] {
    return this.tabNavigationService.formatTabs([
      {
        dgat: 'profile-d6f',
        isAuthorized: this.profileOverviewGuard.isAuthorized,
        routerLink: this.useUpdatedOverview ? 'overview-v2' : 'overview',
        sortOrder: 1,
        label: this.i18n.UserController_Overview,
      },

      {
        dgat: 'User-Index-382',
        routerLink: 'skills',
        sortOrder: 2,
        label: this.i18n.UserController_Skills,
      },
      {
        dgat: 'User-Index-ace',
        isAuthorized: this.profileCollectionGuardV1.isAuthorized,
        routerLink: 'collection',
        sortOrder: 3,
        label: this.i18n.UserController_Collection,
      },
      {
        dgat: 'User-Index-ace',
        isAuthorized: this.profileCollectionGuardV2.isAuthorized,
        routerLink: 'collection-v2',
        sortOrder: 3,
        label: this.i18n.UserController_Collection,
      },
      {
        dgat: 'User-Index-185',
        isAuthorized: this.profileBaseGuard.isAuthorized,
        isExact: true,
        routerLink: 'insights',
        sortOrder: 13,
        label: this.i18n.Core_Activity,
      },
      {
        dgat: 'User-Index-800',
        isAuthorized: this.profileFlexEdGuard.isAuthorized,
        routerLink: 'flex-ed',
        sortOrder: 14,
        label: this.i18n.FlexEdCtrl_FlexEdTitle,
      },
    ]);
  }

  private initializeHeaderNavigationAndTitle() {
    const tabs = this.getTabs();

    // the tabs generated for profile (that we want to reuse) are relative to profile, but that
    // doesn't work in the context of the header so we'll need to update the router links to not
    // be relative.
    tabs.forEach((tab) => {
      tab.routerLink = `/profile/${this.vanityUrl}/${tab.routerLink}`;
    });

    this.globalNavigationService.register({
      title: this.translateService.instant('AppHeader_ProfileTitle'),
      navigationItems: tabs,
    });
  }
}
